import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import type { ChangeEvent, ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

export type MuiFormSelectOptionT = {
  value: string | number;
  label: string;
  icon?: ReactNode;
  /** Optional MUI sx for icon/display (e.g. color), used in menu and when renderSelectedValue uses it */
  sxProps?: SxProps<Theme>;
};

type MuiFormSelectPropsT = {
  name: string;
  label: string;
  options: MuiFormSelectOptionT[];
  placeholder?: string;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** When true, show a search bar at the top of the dropdown */
  searchable?: boolean;
  /** How to render each option in the dropdown list (default: icon + label) */
  renderOption?: (option: MuiFormSelectOptionT) => ReactNode;
  /** How to render the selected value in the trigger (e.g. icon-only); when set, overrides renderOption for display */
  renderSelectedValue?: (option: MuiFormSelectOptionT) => ReactNode;
  /** Extra sx passed to the Select (e.g. compact height to match WarrQueue) */
  sx?: SxProps<Theme>;
  /** Called when the selected value changes (after form state is updated) */
  onChange?: (value: string | number) => void;
};

const MuiFormSelect = ({
  name,
  label,
  options,
  placeholder = '',
  isLoading = false,
  required = false,
  disabled = false,
  searchable = false,
  renderOption,
  renderSelectedValue,
  sx: sxProp,
  onChange: onChangeProp,
}: MuiFormSelectPropsT): JSX.Element => {
  const { control } = useFormContext();
  const [searchQuery, setSearchQuery] = useState('');

  // Use a ref so renderValue always reads the latest options,
  // even if Controller skips a re-render when only options change.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const renderSelectedValueRef = useRef(renderSelectedValue);
  renderSelectedValueRef.current = renderSelectedValue;

  const renderOptionRef = useRef(renderOption);
  renderOptionRef.current = renderOption;

  const filteredOptions = useMemo((): MuiFormSelectOptionT[] => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, searchQuery, searchable]);

  const baseSx = {
    '& .MuiSelect-select': { fontSize: '13px' },
    '& .MuiInputLabel-root': { fontSize: '13px' },
    '& .MuiFormHelperText-root': { fontSize: '11px' },
  };
  let sxExtra: SxProps<Theme>[] = [];
  if (sxProp != null) {
    sxExtra = Array.isArray(sxProp) ? sxProp : [sxProp];
  }
  const resolvedSx = [baseSx, ...sxExtra] as SxProps<Theme>;

  const handleRenderValue = (v: unknown): React.ReactNode => {
    if (v === '' || v == null) {
      return placeholder ? (
        <Box component="span" sx={{ color: 'text.disabled' }}>
          {placeholder}
        </Box>
      ) : (
        <Box component="span" />
      );
    }
    const currentOptions = optionsRef.current;
    const opt = currentOptions.find(o => String(o.value) === String(v));
    // If the option isn't found (e.g. options still loading), show nothing rather than raw value
    if (opt == null) return <Box component="span" />;
    const rsv = renderSelectedValueRef.current;
    if (rsv != null) return rsv(opt);
    const ro = renderOptionRef.current;
    return ro != null ? ro(opt) : opt.label;
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchQuery('');
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field, fieldState }): React.ReactElement => {
        const hasValue = field.value != null && field.value !== '';
        return (
          <Box>
            <FormControl
              fullWidth
              size="small"
              error={!!fieldState.error}
              disabled={disabled || isLoading}
            >
              <InputLabel
                id={`select-label-${name}`}
                shrink={hasValue || undefined}
                required={required}
                sx={{ fontSize: '13px' }}
              >
                {label}
              </InputLabel>
              <Select
                {...field}
                labelId={`select-label-${name}`}
                label={label}
                value={field.value ?? ''}
                displayEmpty
                notched={hasValue || undefined}
                renderValue={handleRenderValue}
                onClose={(): void => setSearchQuery('')}
                onChange={(e): void => {
                  field.onChange(e);
                  const v = e.target.value;
                  if (v !== '' && v != null) {
                    onChangeProp?.(v as string | number);
                  }
                }}
                MenuProps={{
                  autoFocus: !searchable,
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 0.5,
                      ...(searchable ? { maxHeight: 380 } : {}),
                    },
                  },
                }}
                sx={resolvedSx}
              >
                {searchable && (
                  <ListSubheader sx={{ p: 1, lineHeight: 'unset' }}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      variant="outlined"
                      fullWidth
                      autoFocus
                      onKeyDown={(e): void => {
                        e.stopPropagation();
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Clear search"
                              size="small"
                              onClick={handleClearSearch}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </ListSubheader>
                )}
                {filteredOptions.map(option => (
                  <MenuItem key={String(option.value)} value={option.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {option.icon != null && (
                        <Box sx={option.sxProps ?? undefined} display="flex" alignItems="center">
                          {option.icon}
                        </Box>
                      )}
                      <Box component="span" sx={{ color: 'inherit', fontSize: '13px' }}>
                        {option.label}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
                {searchable && searchQuery.trim() && filteredOptions.length === 0 && (
                  <MenuItem disabled>
                    <Box component="span" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                      No results match your search.
                    </Box>
                  </MenuItem>
                )}
              </Select>
              {fieldState.error?.message != null && (
                <FormHelperText error>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );
      }}
    />
  );
};

export default MuiFormSelect;
